(() => {
  if (window.__cStackPanelInitialized) return;
  window.__cStackPanelInitialized = true;

  const sectionStates = new WeakMap();

  function getState(section) {
    let state = sectionStates.get(section);
    if (!state) {
      state = { pcController: null, spController: null };
      sectionStates.set(section, state);
    }
    return state;
  }

  function startAnimationLoop(callback, signal) {
    let rafId = null;

    function tick() {
      callback();
      rafId = window.requestAnimationFrame(tick);
    }

    tick();

    signal.addEventListener('abort', () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    }, { once: true });
  }

  function init() {
    const sections = Array.from(document.querySelectorAll('.c_stack-panel'));
    if (!sections.length) return;

    const mq = window.matchMedia('(max-width: 767px)');

    function activate() {
      sections.forEach((section) => {
        const state = getState(section);
        if (mq.matches) {
          if (state.pcController) {
            state.pcController.abort();
            state.pcController = null;
          }
          initSP(section, state);
        } else {
          if (state.spController) {
            state.spController.abort();
            state.spController = null;
          }
          initPC(section, state);
        }
      });
    }

    activate();
    mq.addEventListener('change', activate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function initPC(section, state) {
    if (state.pcController) state.pcController.abort();
    state.pcController = new AbortController();
    const { signal } = state.pcController;

    const inner = section.querySelector('.c_stack-panel__inner');
    const sticky = section.querySelector('.c_stack-panel__sticky');
    const images = Array.from(section.querySelectorAll('.c_stack-panel__image'));
    const photos = Array.from(section.querySelectorAll('.c_stack-panel__image img'));
    const contents = Array.from(section.querySelectorAll('.c_stack-panel__text'));
    const total = images.length;
    if (!inner || !sticky || !total) return;

    const step = 100 / Math.max(total - 1, 1);
    const panelScrollVh = 130;

    let startPos = 0;
    let endPos = 1;
    let prevTextCurrent = 0;
    let pinState = null;

    function applyPin(newState) {
      if (newState === 'pinned') {
        const innerRect = inner.getBoundingClientRect();
        const left = `${innerRect.left}px`;
        const width = `${innerRect.width}px`;
        if (pinState !== 'pinned') {
          sticky.style.position = 'fixed';
          sticky.style.top = '0';
          sticky.style.bottom = 'auto';
          sticky.style.right = 'auto';
        }
        if (sticky.style.left !== left) sticky.style.left = left;
        if (sticky.style.width !== width) sticky.style.width = width;
      } else if (newState !== pinState) {
        sticky.style.position = 'absolute';
        sticky.style.left = '0';
        sticky.style.right = '0';
        sticky.style.width = '';
        if (newState === 'after') {
          sticky.style.top = 'auto';
          sticky.style.bottom = '0';
        } else {
          sticky.style.top = '0';
          sticky.style.bottom = 'auto';
        }
      }
      pinState = newState;
    }

    function updatePin() {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.top <= 0 && rect.bottom >= vh) {
        applyPin('pinned');
      } else if (rect.bottom < vh) {
        applyPin('after');
      } else {
        applyPin('before');
      }
    }

    function getProgressFromScrollY(scrollY) {
      if (!endPos) return 0;
      let progress = ((scrollY - startPos) / endPos) * 100;
      progress = Math.min(Math.max(progress, 0), 100);
      return progress;
    }

    function calcPositions() {
      inner.style.height = `${total * panelScrollVh}vh`;
      startPos = section.offsetTop;
      endPos = inner.offsetHeight - sticky.offsetHeight;
    }

    calcPositions();
    window.addEventListener('resize', calcPositions, { signal });

    photos.forEach((photo) => { photo.style.transform = ''; });
    images.forEach((image, i) => {
      image.style.clipPath = i === 0
        ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
        : 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)';
    });
    contents.forEach((content, i) => {
      content.style.opacity = i === 0 ? '1' : '0';
    });

    function render(progress) {
      const animating = Math.min(Math.floor(progress / step) + 1, total - 1);
      const localProgress = progress - step * (animating - 1);
      const transitionT = Math.min(Math.max(localProgress / step, 0), 1);

      images.forEach((image, i) => {
        const photo = photos[i];
        const parallaxProgress = progress - step * i;

        if (i < animating) {
          image.style.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
        } else if (i === animating) {
          const clipY = (1 - transitionT) * 100;
          image.style.clipPath = `polygon(0% ${clipY}%, 100% ${clipY}%, 100% 100%, 0% 100%)`;
        } else {
          image.style.clipPath = 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)';
        }

        if (photo && (i === animating - 1 || i === animating || i === animating + 1)) {
          photo.style.transform = `translateY(${-0.5 * parallaxProgress}%)`;
        }
      });

      const textCurrent = Math.min(
        Math.max(transitionT >= 0.5 ? animating : animating - 1, 0),
        total - 1
      );
      if (prevTextCurrent !== textCurrent) {
        if (contents[prevTextCurrent]) contents[prevTextCurrent].style.opacity = '0';
        if (contents[textCurrent]) contents[textCurrent].style.opacity = '1';
        prevTextCurrent = textCurrent;
      }
    }

    function animate() {
      updatePin();
      render(getProgressFromScrollY(window.scrollY));
    }

    signal.addEventListener('abort', () => {
      sticky.style.position = '';
      sticky.style.top = '';
      sticky.style.left = '';
      sticky.style.right = '';
      sticky.style.bottom = '';
      sticky.style.width = '';
    }, { once: true });

    updatePin();
    render(getProgressFromScrollY(window.scrollY));
    startAnimationLoop(animate, signal);
  }

  function initSP(section, state) {
    if (state.spController) state.spController.abort();
    state.spController = new AbortController();
    const { signal } = state.spController;

    const inner = section.querySelector('.c_stack-panel__inner');
    if (inner) inner.style.removeProperty('height');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const contents = Array.from(section.querySelectorAll('.c_stack-panel__sp-content'));
    if (prefersReducedMotion) {
      contents.forEach((el) => el.classList.add('-visible'));
    } else {
      const contentObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('-visible');
          contentObserver.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
      contents.forEach((el) => contentObserver.observe(el));
    }

    const items = Array.from(section.querySelectorAll('.c_stack-panel__sp-image'))
      .map((frameEl) => {
        const moveEl = frameEl.querySelector('[data-sp-photo] img');
        return moveEl ? { frameEl, moveEl } : null;
      })
      .filter(Boolean);
    if (!items.length) return;

    if (prefersReducedMotion) {
      items.forEach((item) => { item.moveEl.style.transform = ''; });
      return;
    }

    const maxShiftPx = 28;
    const photoEase = 0.07;
    const spring = 0.06;
    const damp = 0.78;
    const states = items.map((item) => ({
      frameEl: item.frameEl,
      moveEl: item.moveEl,
      target: 0,
      current: 0,
      top: 0,
      height: 0,
    }));
    let smoothScrollY = window.scrollY;
    let scrollVel = 0;

    function calcPhotoMetrics() {
      const pageY = window.scrollY;
      states.forEach((s) => {
        const rect = s.frameEl.getBoundingClientRect();
        s.top = rect.top + pageY;
        s.height = rect.height;
      });
    }

    states.forEach((s) => {
      s.moveEl.addEventListener('load', calcPhotoMetrics, { signal });
    });

    function getTargetShift(s) {
      const vh = window.innerHeight || 1;
      const center = (s.top - smoothScrollY) + (s.height / 2);
      const normalized = ((center / vh) - 0.5) * 2;
      const clamped = Math.max(Math.min(normalized, 1), -1);
      return clamped * maxShiftPx;
    }

    function updateTargets() {
      states.forEach((s) => {
        s.target = getTargetShift(s);
      });
    }

    function animate() {
      scrollVel += (window.scrollY - smoothScrollY) * spring;
      scrollVel *= damp;
      smoothScrollY += scrollVel;
      if (Math.abs(window.scrollY - smoothScrollY) < 0.08 && Math.abs(scrollVel) < 0.05) {
        smoothScrollY = window.scrollY;
        scrollVel = 0;
      }

      updateTargets();

      states.forEach((s) => {
        s.current += (s.target - s.current) * photoEase;
        if (Math.abs(s.target - s.current) < 0.05) {
          s.current = s.target;
        }
        s.moveEl.style.transform = `translate3d(0, ${s.current.toFixed(3)}px, 0)`;
      });
    }

    window.addEventListener('resize', calcPhotoMetrics, { signal });
    window.addEventListener('orientationchange', calcPhotoMetrics, { signal });
    window.addEventListener('load', calcPhotoMetrics, { signal });

    signal.addEventListener('abort', () => {
      states.forEach((s) => { s.moveEl.style.transform = ''; });
    }, { once: true });

    calcPhotoMetrics();
    updateTargets();
    startAnimationLoop(animate, signal);
  }
})();
